import { useFormDetailsQuery, setFormDetailsQuery } from '../../lib/Queries';
import Loading from '../Loading';
import { EyeIcon } from '@heroicons/react/24/outline/index.js';
import SectionPicker from '../SectionPicker.jsx';
import Design from './Design.jsx';
import Share from './Share.jsx';
import FlowBuilder from '../flows/FlowBuilder.jsx';
import { useState } from 'react';
import PaintPaletteIcon from '../icons/PaintPaletteIcon.jsx';
import FormStylesPanel from './FormStylesPanel.jsx';
import PencilSquareIcon from '../icons/PencilSquareIcon';
import RenameFormModal from './RenameFormModal.jsx';
import { secureHref } from '../../lib/Utils.js';
import PageHeading from '~/components/PageHeading';
import { ObjectTypes } from '~/lib/ObjectTypes.js';
import InfoMessageBlock from '../InfoMessageBlock.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';

const navSections = [
    { value: 'design', label: 'Design' },
    { value: 'automations', label: 'Automations' },
    { value: 'share', label: 'Share' },
];

const EditForm = ({ formId, initialTab }) => {
    const { data, isLoading } = useFormDetailsQuery(formId);

    const [isStylePanelOpen, setIsStylePanelOpen] = useState(false);
    const [selectedNavSection, setSelectedNavSection] = useState(
        initialTab || 'design',
    );
    const [isRenaming, setIsRenaming] = useState(false);

    const form = data?.form || {};

    const { save: saveEnable, isSaving: isEnabling } = useBusinessPostApi(
        `/forms/${formId}/enable`,
    );

    function handleEnableForm() {
        saveEnable().then(result => {
            // Update the form details query with the new enabled state
            setFormDetailsQuery(formId, {
                ...data,
                form: { ...form, disabled: false }
            });
        });
    }

    if (isLoading) {
        return <Loading className="text-2xl mt-8" />;
    }

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <PageHeading
                        heading={
                            <div className="flex items-center">
                                <div>{form.name}</div>
                                <button
                                    type="button"
                                    className="btn-light-pill-sm uppercase ml-2"
                                    onClick={() => setIsRenaming(true)}
                                >
                                    Settings
                                </button>
                            </div>
                        }
                        crumbs={[{ name: 'Forms', href: '/forms' }]}
                    />
                </div>
            </div>

            {!isLoading && (
                <>
                    {form.disabled && (
                        <InfoMessageBlock className="mb-4 max-w-2xl">
                            <div className="flex items-center">
                                <div>
                                    <strong>This form is currently disabled.</strong> It will not accept new submissions from visitors.
                                </div>
                                <button
                                    onClick={handleEnableForm}
                                    disabled={isEnabling}
                                    className="btn-pill ml-4 text-nowrap"
                                >
                                    {isEnabling ? 'Enabling...' : 'Enable Form'}
                                </button>
                            </div>
                        </InfoMessageBlock>
                    )}

                    <div className="sm:flex mb-4 items-center justify-between max-w-3xl 2xl:max-w-[1050px]">
                        <SectionPicker
                            value={selectedNavSection}
                            onChange={setSelectedNavSection}
                            options={navSections}
                            className="mb-2 sm:mb-0"
                        />

                        {/* Centered under the section picker and fully labelled
                            on small screens, where there is no hover to reveal
                            the Preview label; right-aligned from sm up */}
                        <div className="flex items-center justify-center sm:justify-end">
                            {selectedNavSection === 'design' && (
                                <button
                                    className="btn-pink-pill uppercase font-semibold flex items-center mr-3"
                                    type="button"
                                    onClick={() => setIsStylePanelOpen(true)}
                                >
                                    <PaintPaletteIcon className="w-5 h-5 mr-2" />
                                    Styles
                                </button>
                            )}
                            <a
                                href={secureHref(data?.url)}
                                target="_blank"
                                className="flex items-center btn-light-pill !px-6 sm:!px-3 sm:hover:!px-6 uppercase font-semibold group"
                            >
                                <EyeIcon className="w-5 h-5" />
                                <span className="transition-all overflow-hidden max-w-24 opacity-100 ml-2 sm:max-w-0 sm:opacity-0 sm:ml-0 sm:group-hover:opacity-100 sm:group-hover:max-w-24 sm:group-hover:ml-2 text-nowrap">
                                    Preview
                                </span>
                            </a>
                        </div>
                    </div>

                    <FormStylesPanel
                        isOpen={isStylePanelOpen}
                        onClose={() => setIsStylePanelOpen(false)}
                        formId={formId}
                    />

                    {selectedNavSection === 'design' && (
                        <Design
                            formId={formId}
                            hasClientIdentifier={data?.hasClientIdentifier}
                            onClickHeader={() => setIsStylePanelOpen(true)}
                            onClickName={() => setIsRenaming(true)}
                            url={secureHref(data?.url)}
                        />
                    )}

                    {selectedNavSection === 'share' && (
                        <Share
                            form={form}
                            url={secureHref(data?.url)}
                            embedPrefix={data?.embedPrefix}
                            hasClientIdentifier={data?.hasClientIdentifier}
                        />
                    )}

                    {selectedNavSection === 'automations' && (
                        <FlowBuilder
                            flowableId={form.id}
                            flowableType={ObjectTypes.FORM}
                        />
                    )}
                </>
            )}

            <RenameFormModal
                isOpen={isRenaming}
                onClose={() => setIsRenaming(false)}
                form={form}
            />
        </div>
    );
};

export default EditForm;
